import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, FileCheck, ArrowRight, Check, Scale, FileWarning, ShieldCheck, Eye } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { dmccContent } from '@/data/mock';
import Seo from '@/components/Seo';

const keyPointIcons = [AlertTriangle, FileWarning, Eye, Scale];
const helpIcons = [FileCheck, Shield, ShieldCheck, Eye];

export default function DMCCPage() {
  return (
    <Layout>
      <Seo
        title="DMCC Fake Review Compliance"
        description="Understand the UK's DMCC Act on fake reviews and how yBro helps you stay compliant."
        path="/dmcc-compliance"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 to-slate-800 py-24 lg:py-32">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIj48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLThoLTJ2LTRoMnY0em0tOCA4aC0ydi00aDJ2NHptMC04aC0ydi00aDJ2NHoiLz48L2c+PC9nPjwvc3ZnPg==')]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-ybro-primary/15 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ybro-primary/20 border border-ybro-primary/30 mb-8">
              <Shield className="w-4 h-4 text-ybro-primary" />
              <span className="text-sm font-medium font-body text-ybro-primary">Compliance & Risk Assessment</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold font-heading text-white mb-8 leading-tight">
              {dmccContent.pageTitle}
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed font-body">
              {dmccContent.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Key Points Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">Understanding The Law</span>
            <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
              What the DMCC Act says about fake reviews
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {dmccContent.keyPoints.map((point, index) => {
              const IconComponent = keyPointIcons[index];
              return (
                <Card key={index} className="border-slate-200 hover:border-amber-300 hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                        {IconComponent && <IconComponent className="w-6 h-6 text-amber-600" />}
                      </div>
                      <CardTitle className="text-lg sm:text-xl font-bold font-heading text-slate-900 leading-snug">
                        {point.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm sm:text-base text-slate-500 leading-relaxed font-body">{point.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Disclaimer */}
          <div className="mt-12 p-6 rounded-xl bg-ybro-light/50 border border-ybro-primary/20">
            <p className="text-sm text-slate-500 italic text-center font-body">
              {dmccContent.disclaimer}
            </p>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-24 lg:py-32 bg-ybro-light/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-ybro-primary font-semibold text-sm uppercase tracking-wider mb-4 block font-body">
                {dmccContent.howWeHelp.subtitle}
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
                {dmccContent.howWeHelp.title}
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8 font-body">
                {dmccContent.howWeHelp.description}
              </p>

              <div className="space-y-6">
                {dmccContent.howWeHelp.areas.map((area, index) => {
                  const IconComponent = helpIcons[index];
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-ybro-light flex items-center justify-center flex-shrink-0">
                        {IconComponent && <IconComponent className="w-5 h-5 text-ybro-primary" />}
                      </div>
                      <div>
                        <h3 className="font-semibold font-heading text-slate-900 mb-1">{area.title}</h3>
                        <p className="text-slate-500 font-body">{area.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Our Policy Card */}
            <div className="sticky top-32">
              <Card className="border-ybro-primary/30 bg-ybro-light/50">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-6 h-6 text-ybro-primary" />
                    <CardTitle className="text-xl font-bold font-heading text-slate-900">
                      {dmccContent.ownPolicy.title}
                    </CardTitle>
                  </div>
                  <p className="text-sm text-slate-500 font-body">Our commitment to ethical practice</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dmccContent.ownPolicy.points.map((point, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-ybro-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Check className="w-3 h-3 text-ybro-primary" />
                        </div>
                        <span className="text-slate-700 text-sm font-body">{point}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Note */}
              <p className="mt-6 text-sm text-slate-500 italic px-4 font-body">
                Note: We are not a law firm and do not provide formal legal advice. We help you operationalise best practice and work alongside your legal counsel where needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold font-heading text-slate-900 mb-6">
            Protect your business from DMCC risks
          </h2>
          <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto font-body">
            Get a comprehensive review of your current practices and actionable steps to ensure compliance.
          </p>
          <Button 
            asChild
            size="lg"
            className="bg-ybro-primary hover:bg-ybro-dark text-white shadow-xl shadow-ybro-primary/25 hover:shadow-ybro-primary/40 transition-all duration-300 text-base px-8 py-6 h-auto font-body"
          >
            <Link to="/contact" className="flex items-center gap-2">
              Get Your Risk Assessment
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
}
